import React, { useState, useEffect } from 'react';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { LeadStage } from '../types/Lead';
import api from '../services/api';

interface SegmentChangeWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newStageId: number) => void;
  currentStageName: string;
  newSegmentIds: number[];
  newSegmentNames: string[];
}

const SegmentChangeWarningModal: React.FC<SegmentChangeWarningModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  currentStageName,
  newSegmentIds,
  newSegmentNames
}) => {
  const [availableStages, setAvailableStages] = useState<LeadStage[]>([]);
  const [selectedStageId, setSelectedStageId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && newSegmentIds.length > 0) {
      loadAvailableStages();
    }
  }, [isOpen, newSegmentIds]);

  const loadAvailableStages = async () => {
    setLoading(true);
    try {
      const segmentIdsParam = newSegmentIds.join(',');
      const response = await api.get(`/leads/stages?segmentIds=${segmentIdsParam}`);
      
      if (response.data.success) {
        setAvailableStages(response.data.data || []);
        
        // Auto-select first global stage as default
        const defaultStage = response.data.data.find((s: LeadStage) => s.isDefault && s.isGlobal);
        const firstGlobalStage = response.data.data.find((s: LeadStage) => s.isGlobal);
        setSelectedStageId(defaultStage?.id || firstGlobalStage?.id || null);
      }
    } catch (error) {
      console.error('Error loading available stages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (selectedStageId) {
      onConfirm(selectedStageId);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-orange-600" />
            <h2 className="text-xl font-semibold text-gray-900">Segment Change Warning</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Warning Message */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <p className="text-sm text-orange-800">
              You are changing the customer segment for this lead. The current stage 
              <strong className="font-semibold"> "{currentStageName}" </strong> 
              may not be available for the new segment(s).
            </p>
          </div>

          {/* New Segments */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">
              New Segment(s):
            </p>
            <div className="flex flex-wrap gap-2">
              {newSegmentNames.map((name, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                >
                  {name}
                </span>
              ))}
            </div>
          </div>

          {/* Stage Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select New Stage <span className="text-red-500">*</span>
            </label>
            
            {loading ? (
              <div className="text-center py-4">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <p className="text-sm text-gray-600 mt-2">Loading available stages...</p>
              </div>
            ) : (
              <select
                value={selectedStageId || ''}
                onChange={(e) => setSelectedStageId(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a stage</option>
                
                {/* Group by global vs segment-specific */}
                {availableStages.filter(s => s.isGlobal).length > 0 && (
                  <optgroup label="Global Stages">
                    {availableStages
                      .filter(s => s.isGlobal)
                      .map(stage => (
                        <option key={stage.id} value={stage.id}>
                          {stage.name}
                        </option>
                      ))
                    }
                  </optgroup>
                )}
                
                {availableStages.filter(s => !s.isGlobal).length > 0 && (
                  <optgroup label="Segment-Specific Stages">
                    {availableStages
                      .filter(s => !s.isGlobal)
                      .map(stage => (
                        <option key={stage.id} value={stage.id}>
                          {stage.name} ({stage.segmentCodes})
                        </option>
                      ))
                    }
                  </optgroup>
                )}
              </select>
            )}
            
            {availableStages.length === 0 && !loading && (
              <p className="text-sm text-red-600 mt-2">
                No stages available for the selected segment(s). Please create stages first.
              </p>
            )}
          </div>

          {/* Help Text */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800">
              💡 <strong>Tip:</strong> Global stages are available to all leads. Segment-specific stages only appear when their assigned segments are selected.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-white"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedStageId || loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirm & Update
          </button>
        </div>
      </div>
    </div>
  );
};

export default SegmentChangeWarningModal;
